import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminFetch } from '@/hooks/admin/useAdminApi';
import { supabase } from '@/lib/supabase';
import type { BillingRole } from './useAdminCustomers';

/**
 * Per-customer detail loader for the /admin/customers/:customerId page.
 *
 * Reads orders.customers (the full enabled row) + orders.customer_locations
 * directly via PostgREST. The superadmin RLS SELECT policies (added in
 * 20260528000001_admin_section.sql) let the caller see any customer + its
 * locations regardless of their own customer_id linkage.
 *
 * This intentionally mirrors the per-row shape the Customers LIST returns
 * (admin-list-qbo-customers) so the billing-comms editor + feature toggles —
 * which already drive off that shape — work unchanged on the detail page.
 * No new backend function: the existing mutation endpoints
 * (admin-set-billing-comms, admin-set-customer-features, etc.) still own the
 * writes.
 */

export interface AdminCustomerLocation {
  id: string;
  qbo_sub_customer_id: number | null;
  location_code: string | null;
  display_name: string;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  delivery_notes: string | null;
  /** Direct line at this delivery location (the store's own phone). */
  phone: string | null;
  active: boolean;
  /** This location is the account bill-to — at most one per customer. */
  is_billing: boolean;
}

export interface AdminCustomerDetail {
  id: string;
  qbo_customer_id: number;
  name: string;
  account_number: string | null;
  payment_terms: string | null;
  credit_limit: number | null;
  on_credit_hold: boolean;
  active: boolean;
  features: Record<string, boolean> | null;
  invoice_emails_enabled: boolean | null;
  billing_email: string | null;
  accounting_email: string | null;
  remittance_email: string | null;
  optional_email: string | null;
  reminders_enabled: boolean | null;
  statements_enabled: boolean | null;
  invoice_recipients: BillingRole[] | null;
  statement_recipients: BillingRole[] | null;
  reminder_recipients: BillingRole[] | null;
  order_update_recipients: BillingRole[] | null;
  paper_statements_enabled: boolean | null;
  paper_invoices_enabled: boolean | null;
  /** Which billing document(s) the bulk run sends: statement | invoice | both. */
  billing_doc_type: 'statement' | 'invoice' | 'both';
  /** The chain's own store number for this account ("0055"). NULL for
   *  standalone customers; what an emailed purchase order routes on. */
  store_code: string | null;
  /** Manual "switched over to the new system" tracker (checkbox on Overview). */
  on_new_system: boolean;
  bp_unenrolled_at: string | null;
  bp_unenroll_note: string | null;
  locations: AdminCustomerLocation[];
  /**
   * Inferred account shape. A "master" account has one or more sub-customer
   * locations (qbo_sub_customer_id IS NOT NULL); a 1:1 account has a single
   * PRIMARY location (qbo_sub_customer_id IS NULL). See the
   * 20260614000003_primary_locations_backfill migration for the model.
   */
  location_mode: '1:1' | 'master';
  /** The single primary location for 1:1 accounts (null for masters). */
  primary_location: AdminCustomerLocation | null;
}

const CUSTOMER_COLS =
  'id,qbo_customer_id,name,account_number,payment_terms,credit_limit,' +
  'on_credit_hold,active,features,invoice_emails_enabled,billing_email,' +
  'accounting_email,remittance_email,optional_email,reminders_enabled,statements_enabled,' +
  'invoice_recipients,statement_recipients,reminder_recipients,order_update_recipients,' +
  'paper_statements_enabled,paper_invoices_enabled,billing_doc_type,' +
  'on_new_system,bp_unenrolled_at,bp_unenroll_note,store_code';

export function useAdminCustomerDetail(customerId: string | null | undefined) {
  return useQuery<AdminCustomerDetail | null>({
    queryKey: ['admin', 'customer-detail', customerId],
    enabled: !!customerId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data: customer, error: cErr } = await supabase
        .schema('orders')
        .from('customers')
        .select(CUSTOMER_COLS)
        .eq('id', customerId!)
        .maybeSingle();
      if (cErr) throw cErr;
      if (!customer) return null;

      const { data: locs, error: lErr } = await supabase
        .schema('orders')
        .from('customer_locations')
        .select(
          'id,qbo_sub_customer_id,location_code,display_name,address_line1,address_line2,city,state,zip,delivery_notes,phone,active,is_billing',
        )
        .eq('customer_id', customerId!)
        .order('display_name');
      if (lErr) throw lErr;

      const locations = (locs ?? []) as unknown as AdminCustomerLocation[];
      const subs = locations.filter((l) => l.qbo_sub_customer_id != null);
      const locationMode: '1:1' | 'master' = subs.length > 0 ? 'master' : '1:1';
      const primary =
        locationMode === '1:1' ? locations[0] ?? null : null;

      const c = customer as unknown as Record<string, unknown>;
      return {
        id: c.id as string,
        qbo_customer_id: c.qbo_customer_id as number,
        name: c.name as string,
        account_number: (c.account_number as string | null) ?? null,
        payment_terms: (c.payment_terms as string | null) ?? null,
        credit_limit: (c.credit_limit as number | null) ?? null,
        on_credit_hold: c.on_credit_hold === true,
        active: c.active === true,
        features: (c.features as Record<string, boolean> | null) ?? {},
        invoice_emails_enabled: c.invoice_emails_enabled === true,
        billing_email: (c.billing_email as string | null) ?? null,
        accounting_email: (c.accounting_email as string | null) ?? null,
        remittance_email: (c.remittance_email as string | null) ?? null,
        optional_email: (c.optional_email as string | null) ?? null,
        reminders_enabled: c.reminders_enabled === true,
        statements_enabled: c.statements_enabled === true,
        invoice_recipients: (c.invoice_recipients as BillingRole[] | null) ?? ['billing'],
        statement_recipients: (c.statement_recipients as BillingRole[] | null) ?? ['billing'],
        reminder_recipients: (c.reminder_recipients as BillingRole[] | null) ?? ['billing'],
        order_update_recipients: (c.order_update_recipients as BillingRole[] | null) ?? ['billing'],
        paper_statements_enabled: c.paper_statements_enabled === true,
        paper_invoices_enabled: c.paper_invoices_enabled === true,
        billing_doc_type:
          c.billing_doc_type === 'invoice' || c.billing_doc_type === 'both'
            ? (c.billing_doc_type as 'invoice' | 'both')
            : 'statement',
        store_code: (c.store_code as string | null) ?? null,
        on_new_system: c.on_new_system === true,
        bp_unenrolled_at: (c.bp_unenrolled_at as string | null) ?? null,
        bp_unenroll_note: (c.bp_unenroll_note as string | null) ?? null,
        locations,
        location_mode: locationMode,
        primary_location: primary,
      };
    },
  });
}

/**
 * Rename an account. The portal is the customer master, so this writes here
 * first and then pushes the new name to QuickBooks as `DisplayName`
 * (best-effort — a refusal, e.g. a duplicate name, comes back in
 * `push_notes` rather than failing the rename).
 */
export function useSetCustomerName(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      adminFetch<{ ok: true; changed: boolean; name: string; push_notes: string[] }>(
        'admin-set-customer-name',
        { method: 'POST', body: JSON.stringify({ customer_id: customerId, name }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customer-detail', customerId] });
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
      qc.invalidateQueries({ queryKey: ['admin', 'customer-group'] });
    },
  });
}
